import { useCallback, useEffect, useRef, useState } from "react"
import { v4 as uuid } from "uuid"
import {
  addTab as addTabTo,
  canAddTab,
  closeTab as closeTabIn,
  findActiveTab,
  loadTabsState,
  openRouteInTab,
  persistTabsState,
  PlanRouteSnapshot,
  PlanRouteTab,
  PlanRouteTabsState,
  renameTab as renameTabIn,
  restoreTab as restoreTabIn,
  selectTab as selectTabIn,
  updateTabSnapshot as updateTabSnapshotIn,
} from "@/components/analyzeOrderOnPage/planRouteTabs"

// Typing in the driver message changes the snapshot on every keystroke, and a
// tab holds a whole route with alternatives, so writes are batched.
const PERSIST_DELAY_MS = 500

export type ClosedPlanRouteTab = { tab: PlanRouteTab; index: number }

export function usePlanRouteTabs(storageKey: string) {
  const [state, setState] = useState(() => loadTabsState(storageKey))
  // The planner is mounted under this key. It changes whenever another tab
  // becomes active or the active tab gets a different route, so the planner
  // starts again from that tab's snapshot. Edits made inside a tab flow back
  // through updateTabSnapshot without changing it.
  const [plannerKey, setPlannerKey] = useState(() => uuid())

  // The latest state, updated synchronously so that several changes in one
  // event build on each other.
  const stateRef = useRef(state)
  const loadedRef = useRef({ key: storageKey, state })
  const pendingRef = useRef<{ key: string; state: PlanRouteTabsState } | null>(
    null,
  )

  const flush = useCallback(() => {
    const pending = pendingRef.current
    if (!pending) return
    pendingRef.current = null
    persistTabsState(pending.key, pending.state)
  }, [])

  const apply = useCallback(
    (
      change: (state: PlanRouteTabsState) => PlanRouteTabsState,
      remount = false,
    ) => {
      const previous = stateRef.current
      const next = change(previous)
      if (next === previous) return
      stateRef.current = next
      setState(next)
      if (remount || next.activeTabId !== previous.activeTabId) {
        setPlannerKey(uuid())
      }
    },
    [],
  )

  // Another account signed in on this browser: show its tabs.
  useEffect(() => {
    if (loadedRef.current.key === storageKey) return
    flush()
    const loaded = loadTabsState(storageKey)
    loadedRef.current = { key: storageKey, state: loaded }
    stateRef.current = loaded
    setState(loaded)
    setPlannerKey(uuid())
  }, [storageKey, flush])

  useEffect(() => {
    if (state === loadedRef.current.state) return
    pendingRef.current = { key: loadedRef.current.key, state }
    const timer = setTimeout(flush, PERSIST_DELAY_MS)
    return () => clearTimeout(timer)
  }, [state, flush])

  // Write a batched change before the page goes away or the planner unmounts.
  useEffect(() => {
    window.addEventListener("pagehide", flush)
    return () => {
      window.removeEventListener("pagehide", flush)
      flush()
    }
  }, [flush])

  const selectTab = useCallback(
    (tabId: string) => apply((s) => selectTabIn(s, tabId)),
    [apply],
  )

  const addTab = useCallback(() => apply((s) => addTabTo(s)), [apply])

  const closeTab = useCallback(
    (tabId: string): ClosedPlanRouteTab | undefined => {
      const index = stateRef.current.tabs.findIndex((tab) => tab.id === tabId)
      if (index === -1) return undefined
      const tab = stateRef.current.tabs[index]
      apply((s) => closeTabIn(s, tabId))
      return { tab, index }
    },
    [apply],
  )

  const restoreTab = useCallback(
    ({ tab, index }: ClosedPlanRouteTab) =>
      apply((s) => restoreTabIn(s, tab, index)),
    [apply],
  )

  const renameTab = useCallback(
    (tabId: string, name: string) => apply((s) => renameTabIn(s, tabId, name)),
    [apply],
  )

  const updateTabSnapshot = useCallback(
    (tabId: string, snapshot: PlanRouteSnapshot) =>
      apply((s) => updateTabSnapshotIn(s, tabId, snapshot)),
    [apply],
  )

  const openRoute = useCallback(
    (snapshot: PlanRouteSnapshot) =>
      apply((s) => openRouteInTab(s, snapshot), true),
    [apply],
  )

  return {
    tabs: state.tabs,
    activeTab: findActiveTab(state),
    canAdd: canAddTab(state),
    plannerKey,
    selectTab,
    addTab,
    closeTab,
    restoreTab,
    renameTab,
    updateTabSnapshot,
    openRoute,
  }
}

export type PlanRouteTabsApi = ReturnType<typeof usePlanRouteTabs>
